<fidelity-overlay-page-wrapper
    *transloco="let t; prefix: 'fidelity-app'"
    [headerCaption]="t('account.user-info.heading')"
    [errorComponent]="errorComponent()"
>
    <div
        pageContent
        class="grid h-full grid-cols-1"
        [ngStyle]="{ 'grid-template-rows': fetchingData() ? 'auto' : '1fr auto' }"
        *transloco="let gt"
    >
        @if (fetchingData()) {
            <generic-page-loading-state class="my-auto text-tb-primary"></generic-page-loading-state>
        } @else {
            <div class="overflow-auto p-4" cdkScrollable>
                <h2 class="mb-4 w-full text-start">
                    {{ t('account.user-info.info-title') }}
                </h2>
                <div [formGroup]="customerForm" class="grid w-full grid-cols-1 gap-y-4">
                    <!-- First name label -->
                    <div class="flex items-center">
                        <mat-label>
                            {{ gt('generic.first-name') }}
                        </mat-label>
                        :
                        <p class="mx-1">{{ customerForm.get('firstName').value }}</p>
                    </div>

                    <!-- Last name label -->
                    <div class="flex items-center">
                        <mat-label>
                            {{ gt('generic.last-name') }}
                        </mat-label>
                        :
                        <p class="mx-1">{{ customerForm.get('lastName').value }}</p>
                    </div>

                    <!-- Email label -->
                    <div class="flex items-center">
                        <mat-label>
                            {{ gt('generic.email') }}
                        </mat-label>
                        :
                        <p class="mx-1">{{ customerForm.get('contactEmail').value }}</p>
                    </div>

                    <!-- Divider -->
                    <mat-divider></mat-divider>

                    <!-- Form hints -->
                    <p class="text-secondary mb-2">{{ t('account.user-info.form-hints') }}</p>

                    <!-- Phone number -->
                    <mat-form-field>
                        <mat-label>
                            {{ gt('generic.phone-number') }}
                        </mat-label>
                        <input matInput #phone formControlName="phoneNumber" />
                        <mat-hint align="start">{{
                                gt('hints.max-length', { max: ADDRESS_MAX_LENGTHS.PHONE_NUMBER_A })
                            }}
                        </mat-hint>
                        <mat-hint align="end"
                        >{{ phone.value.length }}/{{ ADDRESS_MAX_LENGTHS.PHONE_NUMBER_A }}
                        </mat-hint>
                        @if (customerForm.get('phoneNumber').hasError('pattern')) {
                            <mat-error>{{ gt('validation.pattern-mismatch') }}</mat-error>
                        }
                        @if (customerForm.get('phoneNumber').hasError('maxlength')) {
                            <mat-error>{{ gt('validation.max-length') }}</mat-error>
                        }
                    </mat-form-field>

                    <!-- Birth date -->
                    <mat-form-field>
                        <mat-label>
                            {{ gt('generic.dob') }}
                        </mat-label>
                        <input matInput [matDatepicker]="picker" formControlName="birthDate" />
                        <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
                        <mat-datepicker #picker></mat-datepicker>
                    </mat-form-field>

                    <!-- Gender -->
                    <mat-form-field>
                        <mat-label>
                            {{ gt('generic.gender') }}
                        </mat-label>
                        <mat-select formControlName="gender">
                            <mat-option [value]="Gender.unspecified"
                            ><i class="text-secondary">{{ gt('genders.null') }}</i>
                            </mat-option>
                            <mat-option [value]="Gender.male">{{ gt('genders.male') }}</mat-option>
                            <mat-option [value]="Gender.female">{{ gt('genders.female') }}</mat-option>
                        </mat-select>
                    </mat-form-field>

                    <!-- Street -->
                    <mat-form-field>
                        <mat-label>
                            {{ gt('components.address-control.street') }}
                        </mat-label>
                        <input
                            matInput
                            [formControlName]="'street'"
                            [placeholder]="gt('components.address-control.street')"
                            [spellcheck]="false"
                            #street
                        />
                        <mat-hint align="start">{{ gt('hints.max-length', { max: ADDRESS_MAX_LENGTHS.STREET }) }}
                        </mat-hint>
                        <mat-hint align="end">{{ street.value.length }}/ {{ ADDRESS_MAX_LENGTHS.STREET }}</mat-hint>
                        @if (customerForm.get('street').hasError('required')) {
                            <mat-error>{{ gt('validation.req') }}</mat-error>
                        }
                        @if (customerForm.get('street').hasError('maxlength')) {
                            <mat-error>{{ gt('validation.max-length') }}</mat-error>
                        }
                    </mat-form-field>

                    <!-- City -->
                    <mat-form-field>
                        <mat-label>
                            {{ gt('components.address-control.city') }}
                        </mat-label>
                        <input
                            matInput
                            [formControlName]="'city'"
                            [placeholder]="gt('components.address-control.city')"
                            [spellcheck]="false"
                            #city
                        />
                        <mat-hint align="start">{{ gt('hints.max-length', { max: ADDRESS_MAX_LENGTHS.CITY }) }}
                        </mat-hint>
                        <mat-hint align="end">{{ city.value.length }}/ {{ ADDRESS_MAX_LENGTHS.CITY }}</mat-hint>
                        @if (customerForm.get('city').hasError('required')) {
                            <mat-error>{{ gt('validation.req') }}</mat-error>
                        }
                        @if (customerForm.get('city').hasError('maxlength')) {
                            <mat-error>{{ gt('validation.max-length') }}</mat-error>
                        }
                    </mat-form-field>

                    <!-- Zip -->
                    <mat-form-field>
                        <mat-label>
                            {{ gt('components.address-control.postal-code') }}
                        </mat-label>
                        <input
                            matInput
                            [formControlName]="'postalCode'"
                            [placeholder]="gt('components.address-control.postal-code')"
                            [spellcheck]="false"
                            #zipCode
                        />
                        <mat-hint align="start">{{
                                gt('hints.max-length', { max: ADDRESS_MAX_LENGTHS.POSTAL_CODE })
                            }}
                        </mat-hint>
                        <mat-hint align="end"
                        >{{ zipCode.value.length }}/ {{ ADDRESS_MAX_LENGTHS.POSTAL_CODE }}
                        </mat-hint
                        >
                        @if (customerForm.get('postalCode').hasError('required')) {
                            <mat-error>{{ gt('validation.req') }}</mat-error>
                        }
                        @if (customerForm.get('postalCode').hasError('maxlength')) {
                            <mat-error>{{ gt('validation.max-length') }}</mat-error>
                        }
                    </mat-form-field>

                    <!-- Province -->
                    <mat-form-field>
                        <mat-label>
                            {{ gt('components.address-control.province') }}
                        </mat-label>
                        <input
                            matInput
                            [formControlName]="'province'"
                            [placeholder]="gt('components.address-control.province')"
                            [spellcheck]="false"
                            #province
                        />
                        <mat-hint align="start">{{ gt('hints.max-length', { max: ADDRESS_MAX_LENGTHS.PROVINCE }) }}
                        </mat-hint>
                        <mat-hint align="end">{{ province.value.length }}/ {{ ADDRESS_MAX_LENGTHS.PROVINCE }}</mat-hint>
                        @if (customerForm.get('province').hasError('required')) {
                            <mat-error>{{ gt('validation.req') }}</mat-error>
                        }
                        @if (customerForm.get('province').hasError('maxlength')) {
                            <mat-error>{{ gt('validation.max-length') }}</mat-error>
                        }
                    </mat-form-field>

                    <!-- Country -->
                    <mat-form-field class="example-full-width">
                        <mat-label>{{ gt('components.address-control.country') }}</mat-label>
                        <input
                            type="text"
                            [placeholder]="gt('components.address-control.country')"
                            matInput
                            [value]="customerForm.value.country?.name"
                            (input)="filterCountries($event)"
                            [matAutocomplete]="auto"
                            autocomplete="off"
                            #countrySearch
                        />
                        <mat-autocomplete
                            autoActiveFirstOption
                            #auto="matAutocomplete"
                            [displayWith]="getOptionName"
                            (optionSelected)="setCountry($event)"
                        >
                            @for (option of filteredCountries(); track option) {
                                <mat-option [value]="option">{{ option.name }}</mat-option>
                            }
                        </mat-autocomplete>
                    </mat-form-field>
                </div>
            </div>

            @if (fullErrorMessage()) {
                <div @fadeIn @fadeOut
                     class="mx-4 bg-red-50 border border-red-400 rounded-md flex items-center text-warn ps-4 pe-1 py-1">
                    <mat-icon svgIcon="heroicons_solid:x-circle" class="text-warn scale-75 mr-2"></mat-icon>
                    <p class="flex-grow">{{ fullErrorMessage() }}</p>
                    <button mat-icon-button (click)="dismissError()">
                        <mat-icon svgIcon="heroicons_outline:x-mark" class="size-4 scale-75 text-warn"></mat-icon>
                    </button>
                </div>
            }

            <button
                matRipple
                mat-flat-button
                [disabled]="disableSave()"
                class="fuse-mat-button-large m-4 mb-0 enabled:bg-tb-primary enabled:text-tb-primary-contrast"
                (click)="saveChanges()"
            >
                @if (disableCrudActions()) {
                    <mat-progress-spinner [diameter]="24" [mode]="'indeterminate'"></mat-progress-spinner>
                } @else {
                    {{ gt('generic.save') }}
                }
            </button>
        }
    </div>
</fidelity-overlay-page-wrapper>
